// One row per shipped widget, for the integration reference on /docs (and so
// /docs.md). public/llms.txt carries the same table by hand;
// src/__tests__/widgetReference.test.js fails if the two drift.
//
// Every value here describes existing behaviour – change the widget, then
// this, never the other way round:
//   type    – the ?type= value WidgetEmbedPage renders (legacyTypes alias it)
//   height  – the iframe height SimpleDashboard's snippet generator uses
//   allow   – the iframe allow list that snippet carries ('' = none needed)
//   needsCid – false when WidgetEmbedPage lists the type in publicWidgetTypes
//   options – what the dashboard can set, stored against the config ID
//   slug    – its page under /tools/ (src/tools.js)
//   category – which WIDGET_CATEGORIES group it is listed under
//   summary – one line on what it does, for the integration guide
export const SCRIPT_TAG_SNIPPET = `<script src="https://widgets.karbonkit.com/embed.js"></script>
<div data-karbonkit data-config-id="YOUR_CONFIG_ID"></div>`;

// The groups every widget list is sorted into (the Tools menu, the landing
// page, /docs, llms.txt, the dashboard's tabs, widgets.json): the question a
// household is asking when the widget helps. `short` is for tight spaces.
export const WIDGET_CATEGORIES = [
  { id: 'right-for-my-home', name: 'Is it right for my home?', short: 'Right for my home?' },
  { id: 'getting-it-done', name: 'How do I get it done?', short: 'Getting it done' },
  { id: 'living-with-it', name: 'What is it like to live with?', short: 'Living with it' },
];

export const categoryOf = (id) => WIDGET_CATEGORIES.find((c) => c.id === id);

// Sort any list of widgets (or tools, or dashboard tabs) into the groups, in
// WIDGET_CATEGORIES order, keeping each list's own order within a group.
// Empty groups are left out.
export const groupByCategory = (items, getCategory) => WIDGET_CATEGORIES
  .map((category) => ({ category, items: items.filter((item) => getCategory(item) === category.id) }))
  .filter((group) => group.items.length > 0);

export const WIDGET_REFERENCE = [
  {
    name: 'Savings calculator',
    type: 'lct-calculator',
    category: 'right-for-my-home',
    slug: 'savings-calculator',
    summary: 'Sizes a heat pump, solar PV and a battery for a UK home from its postcode, its EPC record or the household\'s bills, then estimates costs, savings and carbon.',
    height: 900,
    allow: 'clipboard-write',
    needsCid: true,
    options: 'Brand colour; company name; which technologies to offer (heat pump, solar PV, battery); installer finder after results (on by default); caveat banner (up to 240 characters); enquiry collection (paid feature, off by default)',
  },
  {
    name: 'Heat pump visualiser',
    type: 'heat-pump-ar',
    category: 'living-with-it',
    slug: 'heat-pump-visualiser',
    summary: 'Places a true-size heat pump or hot water cylinder in the visitor\'s own space through their phone camera (WebXR on Android Chrome, AR Quick Look on iOS Safari). Desktop browsers get a 3D viewer.',
    height: 500,
    allow: 'camera; xr-spatial-tracking; accelerometer; gyroscope; magnetometer; fullscreen',
    needsCid: true,
    options: 'Brand colour; background colour; which heat pump model loads first',
  },
  {
    name: 'Sound simulator',
    type: 'sound-simulator',
    category: 'living-with-it',
    slug: 'sound-simulator',
    summary: 'Plays a real heat pump recording and measures its level in decibels with the phone\'s microphone, so a visitor can hear it from different distances.',
    height: 1000,
    allow: 'microphone',
    needsCid: false,
    options: 'Company name (shown as "Provided by …"); brand colour for its buttons and highlights',
  },
  {
    name: '3D house tour',
    type: 'house-tour',
    category: 'living-with-it',
    slug: 'house-tour',
    summary: 'A 3D cutaway of an ordinary 1930s semi that runs entirely on electricity – heat pump, hot water, radiators, solar panels, a home battery, car charging and cooking – with a guided tour and a plain-English note on each of twenty things.',
    legacyTypes: ['system-diagram'],
    height: 660,
    allow: 'fullscreen',
    needsCid: false,
    options: 'Brand colour; company name (shown as "Provided by …"); where the last button goes',
  },
  {
    name: "Watt's House",
    type: 'watts-house',
    category: 'living-with-it',
    slug: 'watts-house',
    summary: 'The same all-electric house, explored with Watt the cat: send him from room to room and whatever he sits beside is explained in plain English.',
    height: 660,
    allow: 'fullscreen',
    needsCid: false,
    options: 'Brand colour; company name (shown as "Provided by …"); where the last button goes',
  },
  {
    name: 'Installer finder',
    type: 'installer-finder',
    category: 'getting-it-done',
    slug: 'installer-finder',
    summary: 'Postcode search for nearby installers, from a directory built from public records, with a link to check each one on the MCS register.',
    height: 700,
    allow: '',
    needsCid: false,
    options: 'Brand colour; company name; installer source (the public directory, your own list, or both); your own suggested installers',
  },
  {
    name: 'Grant finder',
    type: 'grant-finder',
    category: 'getting-it-done',
    slug: 'grant-finder',
    summary: 'A postcode and four questions show which UK funding schemes a household may qualify for, each linked to its official page.',
    height: 900,
    allow: '',
    needsCid: false,
    options: 'Brand colour; company name; link to the visitor\'s council (off by default); your own local schemes (off by default)',
  },
  {
    name: 'Boiler escape plan',
    type: 'boiler-escape-plan',
    category: 'right-for-my-home',
    slug: 'boiler-escape-plan',
    summary: 'The carbon arithmetic of replacing a working gas boiler now versus waiting for it to fail, then a readiness plan with optional reminders.',
    height: 1000,
    allow: '',
    needsCid: false,
    options: 'Company name (shown as "Provided by …"); enquiry collection (paid feature, off by default). The widget keeps its own colours; brand colour is used on the enquiry form and footer link',
  },
  {
    name: 'Radiator check',
    type: 'radiator-check',
    category: 'right-for-my-home',
    slug: 'radiator-check',
    summary: 'Which rooms\' radiators are likely fine for a heat pump, which may need a deeper radiator in the same space and which may need more wall space, from the boiler\'s setting and each radiator\'s type. No tape measure.',
    height: 1100,
    allow: '',
    needsCid: false,
    options: 'Brand colour; company name (shown as "Provided by …"); enquiry collection (paid feature, off by default)',
  },
];

// In preview: deployed and embeddable with a config ID, but listed nowhere
// public yet – not in the Tools menu, /docs, llms.txt, widgets.json or the
// methodology pages, and its dashboard tab shows only at
// /dashboard?tab=ai-assistant (or once the account has one). It is being
// tried on an unlisted electrifiedhome.org page. To launch it, move the row
// into WIDGET_REFERENCE and its group into WIDGET_CATEGORIES, then do what
// they require (llms.txt rows, a methodology entry, a tool page), and add the
// assistant route to /privacy first.
export const PREVIEW_CATEGORIES = [
  { id: 'own-assistant', name: 'Use your own AI assistant', short: 'Your own AI' },
];

export const PREVIEW_WIDGETS = [
  {
    name: 'Bring your own AI',
    type: 'ai-assistant',
    category: 'own-assistant',
    slug: 'ai-assistant',
    summary: 'A starter message, with a copy button, that a household pastes into their own AI assistant (ChatGPT, Claude…), which then takes them through the boiler escape plan or the radiator check using KarbonKit\'s tools. The message is kept up to date centrally and built from your settings, so nothing can be added to it.',
    height: 660,
    allow: 'clipboard-write',
    needsCid: true,
    options: 'Company name; brand colour; which journey it starts (boiler escape plan or radiator check); one link to your own next step',
  },
];

// Every widget the backend accepts, shipped or in preview: for lookups by
// type (the dashboard, saved widgets' names), never for public lists.
export const ALL_WIDGETS = [...WIDGET_REFERENCE, ...PREVIEW_WIDGETS];

// ---------------------------------------------------------------------------
// The integration guide (/docs/integration/, and so /docs/integration.md).
// src/__tests__/integrationGuide.test.js checks each value against the code
// it describes: public/embed.js, SimpleDashboard.js and backend/app.py's
// sanitize_widget_configuration().

export const WIDGET_HOST = 'https://widgets.karbonkit.com';
export const CONFIG_ID_PLACEHOLDER = 'YOUR_CONFIG_ID';

// What public/embed.js does, in numbers.
export const LOADER = {
  initialHeight: 600,           // INITIAL_HEIGHT
  minHeight: 61,                // resize accepted when h > 60 ...
  maxHeight: 4999,              // ... and h < 5000
  idPattern: 'A–Z, a–z, 0–9, _ and -, 8 to 64 characters',
  allow: 'camera; microphone; xr-spatial-tracking; fullscreen; accelerometer; gyroscope; magnetometer; clipboard-write',
  sandbox: 'allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox allow-downloads',
};

// The optional credit line the dashboard appends to every snippet
// (ATTRIBUTION_SNIPPET in SimpleDashboard.js – the test keeps them equal).
export const CREDIT_SNIPPET = `<!-- Optional: you can delete the credit link below -->
<a href="https://www.karbonkit.com/">Heat pump tools by KarbonKit</a>`;

export const scriptTagSnippet = (id = CONFIG_ID_PLACEHOLDER) => `<script src="${WIDGET_HOST}/embed.js"></script>
<div data-karbonkit data-config-id="${id}"></div>`;

// The iframe the dashboard generates for a saved widget, attribute for
// attribute.
export const iframeSnippet = (w, id = CONFIG_ID_PLACEHOLDER) => [
  '<iframe',
  `  src="${WIDGET_HOST}/widget-embed?type=${w.type}&cid=${id}"`,
  '  width="100%"',
  `  height="${w.height}"`,
  '  frameborder="0"',
  '  style="border: none; border-radius: 8px;"',
  ...(w.allow ? [`  allow="${w.allow}"`] : []),
  ...(w.allow.includes('fullscreen') ? ['  allowfullscreen'] : []),
  '></iframe>',
].join('\n');

// Settings saved against a config ID. They are chosen in the dashboard, never
// in the snippet. `field` is the key in the stored configuration (and in
// GET /api/widget/<config id>/config); `def` is what a saved widget gets when
// the owner leaves the setting alone.
const BRAND_COLOUR = {
  setting: 'Brand colour', field: 'primary_color', type: 'hex colour', def: '#2d6a4f',
  allowed: '#RRGGBB (six hex digits). Anything else is saved as #2d6a4f',
};
const COMPANY_NAME = {
  setting: 'Company name', field: 'company_name', type: 'string', def: '(empty)',
  allowed: 'Up to 80 characters',
};
const PLAIN_STYLE = {
  setting: 'Plain style', field: 'plain_style', type: 'boolean', def: 'false',
  allowed: 'true or false. true uses the system font, drops card shadows and the "Provided by" line, so the widget looks like part of your page',
};
const COUNT_VISITS = {
  setting: 'Count visits', field: 'analytics_enabled', type: 'boolean', def: 'true',
  allowed: 'true or false. false records nothing about visits to the widget',
};
const TOUR_NEXT_STEP = [
  { setting: 'Where the last button goes', field: 'tour_next_step.destination', type: 'string', def: 'karbonkit',
    allowed: 'karbonkit (KarbonKit\'s boiler escape plan; no button on a white-label embed), own (your page, below) or none (no button). The button is on the card at the end of the tour and when every point has been found' },
  { setting: 'Your page', field: 'tour_next_step.url', type: 'string', def: '(empty)',
    allowed: 'An https link, used when destination is own (a bare address gets https added). own with no usable link is saved as karbonkit' },
  { setting: 'Button text', field: 'tour_next_step.label', type: 'string', def: '(empty)',
    allowed: 'Up to 40 characters, used when destination is own. Empty shows "See if your own house is ready"' },
];
const LEAD_CAPTURE = {
  setting: 'Enquiry collection (Leads tab)', field: 'lead_capture', type: 'object', def: 'off',
  allowed: 'Paid feature (Pro and above). See "Enquiry collection settings" below',
};

export const WIDGET_OPTIONS = {
  'lct-calculator': [
    { ...BRAND_COLOUR, use: 'Buttons, selected options and the footer link' },
    { ...COMPANY_NAME, use: 'Shown in the calculator header ("KarbonKit" when empty)' },
    { setting: 'Technologies', field: 'available_lcts', type: 'array of strings', def: '["heatPump", "solarPV", "battery"]',
      allowed: 'Any non-empty subset of heatPump, solarPV, battery. An empty list shows all three' },
    { setting: 'Show nearby installers after results', field: 'show_installer_finder', type: 'boolean', def: 'true',
      allowed: 'true or false' },
    { setting: 'Caveat banner', field: 'caveat', type: 'string', def: '(empty)',
      allowed: 'Up to 240 characters, shown as an amber banner at the top' },
    LEAD_CAPTURE,
    PLAIN_STYLE,
    COUNT_VISITS,
  ],
  'heat-pump-ar': [
    { ...BRAND_COLOUR, use: 'The AR button and badges' },
    { setting: 'Background colour', field: 'secondary_color', type: 'hex colour', def: '#fafaf9',
      allowed: '#RRGGBB (six hex digits). Anything else is saved as #fafaf9' },
    { setting: 'Model that loads first', field: 'model_id', type: 'integer', def: '7',
      allowed: 'MODEL_IDS' },
    PLAIN_STYLE,
    COUNT_VISITS,
  ],
  'sound-simulator': [
    { ...COMPANY_NAME, use: 'Shown as "Provided by …"' },
    { ...BRAND_COLOUR, use: 'The play and meter buttons, highlights and the footer link' },
    PLAIN_STYLE,
    COUNT_VISITS,
  ],
  'house-tour': [
    { ...BRAND_COLOUR, use: 'Tour buttons, links, the current room and the footer link' },
    { ...COMPANY_NAME, use: 'Shown under the tour as "Provided by …"' },
    ...TOUR_NEXT_STEP,
    PLAIN_STYLE,
    COUNT_VISITS,
  ],
  'watts-house': [
    { ...BRAND_COLOUR, use: 'Tour buttons, links, the current room and the footer link' },
    { ...COMPANY_NAME, use: 'Shown under the tour as "Provided by …"' },
    ...TOUR_NEXT_STEP,
    PLAIN_STYLE,
    COUNT_VISITS,
  ],
  'installer-finder': [
    { ...BRAND_COLOUR, use: 'Buttons, selected options and the footer link' },
    { ...COMPANY_NAME, use: 'Shown as "Provided by …"' },
    { setting: 'Installer source', field: 'installer_source', type: 'string', def: 'directory',
      allowed: 'directory (the public directory), both (your list first, then the directory), curated (only your list; with no entries yet, the directory is shown)' },
    { setting: 'Your suggested installers', field: 'curated_installers', type: 'array of objects', def: '[]',
      allowed: 'Up to 25. Each needs a name (80 characters); postcode, telephone, website (reduced to https://host), email, technologies (heat_pump, solar_pv, battery) and a note (160 characters) are optional. Shown first, labelled "Suggested by" you' },
    PLAIN_STYLE,
    COUNT_VISITS,
  ],
  'grant-finder': [
    { ...BRAND_COLOUR, use: 'Buttons, selected options and the footer link' },
    { ...COMPANY_NAME, use: 'Shown as "Provided by …"' },
    { setting: 'Link to the visitor\'s council', field: 'grant_finder.show_council_link', type: 'boolean', def: 'false',
      allowed: 'true or false' },
    { setting: 'Your local schemes', field: 'grant_finder.local_schemes', type: 'array of objects', def: '[]',
      allowed: 'Up to 10. Each needs a name (100 characters), an https link and a date to stop showing it (YYYY-MM-DD, at most 3 years ahead). Provider (80), headline (100), summary (400) and council areas (up to 40; none means every visitor) are optional. Labelled "Added by" you' },
    PLAIN_STYLE,
    COUNT_VISITS,
  ],
  'boiler-escape-plan': [
    { ...COMPANY_NAME, use: 'Shown as "Provided by …"' },
    { ...BRAND_COLOUR, use: 'Only the enquiry form and the footer link; the widget keeps its own colours' },
    LEAD_CAPTURE,
    PLAIN_STYLE,
    COUNT_VISITS,
  ],
  'radiator-check': [
    { ...BRAND_COLOUR, use: 'Buttons, selected options, the progress bar and the footer link' },
    { ...COMPANY_NAME, use: 'Shown as "Provided by …"' },
    LEAD_CAPTURE,
    PLAIN_STYLE,
    COUNT_VISITS,
  ],
};

export const LEAD_CAPTURE_OPTIONS = [
  { setting: 'Collect enquiries', field: 'enabled', type: 'boolean', def: 'false',
    allowed: 'Stays false unless a valid notification address is set' },
  { setting: 'Send enquiries to', field: 'notify_email', type: 'email address', def: '(empty)',
    allowed: 'Required to switch collection on. Never sent to the visitor\'s browser' },
  { setting: 'Your organisation\'s name', field: 'owner_label', type: 'string', def: '(empty)',
    allowed: 'Up to 80 characters; names who the visitor agrees to hear from' },
  { setting: 'Consent wording', field: 'consent_text', type: 'string', def: '(empty: standard wording)',
    allowed: 'Up to 300 characters' },
  { setting: 'Ask for a phone number', field: 'ask_phone', type: 'boolean', def: 'false', allowed: 'true or false' },
  { setting: 'Ask what they are hoping to achieve', field: 'ask_goal', type: 'boolean', def: 'false', allowed: 'true or false' },
];
